import {
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Heading,
  Text,
  useDisclosure,
  VStack,
  Divider,
  FormControl,
  FormLabel,
  FormErrorMessage,
} from '@chakra-ui/react';
import React, { useRef, useState } from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import {
  TaskCategoryGQLType,
  TaskStatusGQLType,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { colors } from '~/packages/common-frontend/colors';
import { FullDateRangePicker } from '~/packages/common-frontend/components/DateRangePicker';
import { Icon } from '~/packages/common-frontend/components/icons';
import { IsolatedMultiSelect } from '~/packages/common-frontend/components/MultiSelect';

// styles
const dropdownButtonProps = {
  as: Button,
  minWidth: '10rem',
  variant: 'secondary-muted',
  fontWeight: 400,
  width: '100%',
};
const menuListProps = {
  bgColor: colors.background[100],
  py: '0',
};
const inputProps = {
  backgroundColor: 'white',
};
const menuItemOptionProps = {
  bgColor: colors.background[100],
  _hover: { bg: colors.background[500] },
};

export type FilterState = {
  creditFacilityIds: string[];
  loanIds?: string[];
  categories: TaskCategoryGQLType[];
  statuses: TaskStatusGQLType[];
  dateRange?: {
    startDate: Util.FullDateStr;
    endDate: Util.FullDateStr;
  };
};

export const TaskCenterFiltersDrawer = ({
  onFilterChange,
  currentFilter,
  initialFilter,
  allLoanIds,
  allStatuses = ['overdue', 'dueSoon', 'completed', 'archived'],
  allCategories = ['fundingRequest', 'invoice', 'lenderShares', 'disbursement'],
}: {
  currentFilter: FilterState;
  initialFilter: FilterState;
  onFilterChange: (nextFilters: FilterState) => Promise<void>;
  allLoanIds: string[];
  allStatuses?: TaskStatusGQLType[];
  allCategories?: TaskCategoryGQLType[];
}) => {
  const drawerControls = useDisclosure();
  const { facilities } = useCreditFacilitiesOrThrow();

  // Filters
  const [filters, setFilters] = useState<FilterState>(currentFilter);

  const filtersBtnRef = useRef(null);

  return (
    <>
      <Button
        variant="primary-muted"
        colorScheme="v2-aurora"
        ref={filtersBtnRef}
        onClick={drawerControls.onOpen}
        gap="0.5rem"
      >
        <Icon icon="filter" />
        Filters
      </Button>
      <Drawer
        placement="right"
        finalFocusRef={filtersBtnRef}
        size="xl"
        {...drawerControls}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader textAlign="left" mt="1.5rem" px="3rem" pb="0">
            <Heading>Filters</Heading>
          </DrawerHeader>
          <Divider my="1.5rem" />
          <DrawerBody px="3rem">
            <VStack align="flex-start">
              <FormControl isInvalid={filters.creditFacilityIds.length === 0}>
                <FormLabel>
                  <Heading fontSize="xl">Credit Facilities</Heading>
                  <Text color="gray">
                    Filter tasks associated with specific facilities.
                  </Text>
                </FormLabel>
                <IsolatedMultiSelect
                  autoApplyOptions
                  buttonProps={dropdownButtonProps}
                  initialSelections={filters.creditFacilityIds}
                  inputProps={inputProps}
                  isSearchable
                  matchWidthPopupBreakpoint={400}
                  menuItemOptionProps={menuItemOptionProps}
                  menuListProps={menuListProps}
                  onApplyOptions={(creditFacilityIds) => {
                    setFilters((curr) => ({
                      ...curr,
                      creditFacilityIds,
                    }));
                  }}
                  optionsHeader="Credit facilities"
                  options={facilities.map((facility) => ({
                    id: facility.id,
                    label: facility.name,
                    value: facility.id,
                  }))}
                  placeholderText="Select credit facilities"
                  showSelectedCount
                  titleText="All credit facilities"
                />
                <FormErrorMessage>
                  Select at least one facility.
                </FormErrorMessage>
              </FormControl>

              <Divider my="1.5rem" />
              <FormControl>
                <FormLabel>
                  <Heading fontSize="xl">Loans</Heading>
                  <Text color="gray">Filter by loan.</Text>
                </FormLabel>
                <IsolatedMultiSelect
                  autoApplyOptions
                  buttonProps={dropdownButtonProps}
                  initialSelections={filters.loanIds ?? []}
                  inputProps={inputProps}
                  isSearchable
                  matchWidthPopupBreakpoint={400}
                  menuItemOptionProps={menuItemOptionProps}
                  menuListProps={menuListProps}
                  onApplyOptions={(loanIds) => {
                    setFilters((curr) => ({ ...curr, loanIds }));
                  }}
                  optionsHeader="Loans"
                  options={allLoanIds.map((loanId) => ({
                    id: loanId,
                    value: loanId,
                    label: loanId,
                  }))}
                  placeholderText="Select loans"
                  showSelectedCount
                  titleText="All loans"
                />
              </FormControl>

              <Divider my="1.5rem" />
              <FormControl isInvalid={filters.categories.length === 0}>
                <FormLabel>
                  <Heading fontSize="xl">Category</Heading>
                  <Text color="gray">Filter by categories.</Text>
                </FormLabel>
                <IsolatedMultiSelect
                  autoApplyOptions
                  buttonProps={dropdownButtonProps}
                  initialSelections={filters.categories}
                  inputProps={inputProps}
                  isSearchable
                  matchWidthPopupBreakpoint={400}
                  menuItemOptionProps={menuItemOptionProps}
                  menuListProps={menuListProps}
                  onApplyOptions={(categories) => {
                    setFilters((curr) => ({ ...curr, categories }));
                  }}
                  optionsHeader="Categories"
                  options={allCategories.map((category) => ({
                    id: category,
                    label: commonUtils.toTitleCase(category),
                    value: category,
                  }))}
                  placeholderText="Select categories"
                  showSelectedCount
                  titleText="All categories"
                />
                <FormErrorMessage>
                  Select at least one category.
                </FormErrorMessage>
              </FormControl>

              <Divider my="1.5rem" />
              <FormControl isInvalid={filters.statuses.length === 0}>
                <FormLabel>
                  <Heading fontSize="xl">Status</Heading>
                  <Text color="gray">Filter by statuses.</Text>
                </FormLabel>
                <IsolatedMultiSelect
                  autoApplyOptions
                  buttonProps={dropdownButtonProps}
                  initialSelections={filters.statuses}
                  inputProps={inputProps}
                  isSearchable
                  matchWidthPopupBreakpoint={400}
                  menuItemOptionProps={menuItemOptionProps}
                  menuListProps={menuListProps}
                  onApplyOptions={(statuses) => {
                    setFilters((curr) => ({ ...curr, statuses }));
                  }}
                  optionsHeader="Status"
                  options={allStatuses.map((status) => ({
                    id: status,
                    label: commonUtils.toTitleCase(status),
                    value: status,
                  }))}
                  placeholderText="Select statuses"
                  showSelectedCount
                  titleText="All statuses"
                />
                <FormErrorMessage>Select at least one status.</FormErrorMessage>
              </FormControl>

              <Divider my="1.5rem" />
              <FormControl>
                <FormLabel>
                  <Heading fontSize="xl">Due Dates</Heading>
                  <Text color="gray">
                    Find tasks due within a particular range.
                  </Text>
                </FormLabel>
                <FullDateRangePicker
                  onSelectDateRange={(newDateRange) => {
                    const [newStartDate, newEndDate] = newDateRange;
                    if (newStartDate && newEndDate) {
                      setFilters((curr) => ({
                        ...curr,
                        dateRange: {
                          startDate: commonDate.dateToFullDateString(
                            newStartDate,
                          ),
                          endDate: commonDate.dateToFullDateString(newEndDate),
                        },
                      }));
                    }
                    if (newStartDate === null && newEndDate === null) {
                      setFilters((curr) => ({
                        ...curr,
                        dateRange: undefined,
                      }));
                    }
                  }}
                  currentDateRange={{
                    startDate: filters.dateRange
                      ? commonDate.fullDateStrToDate(
                          filters.dateRange.startDate,
                        )
                      : null,
                    endDate: filters.dateRange
                      ? commonDate.fullDateStrToDate(filters.dateRange.endDate)
                      : null,
                  }}
                />
              </FormControl>
            </VStack>
          </DrawerBody>
          <Divider />
          <DrawerFooter justifyContent="space-between" m="1.5rem">
            <Button
              variant="secondary"
              colorScheme="v2-aurora"
              onClick={() => {
                setFilters(initialFilter);
                onFilterChange(initialFilter);
                drawerControls.onClose();
              }}
            >
              Reset Filters
            </Button>
            <Button
              isDisabled={
                filters.creditFacilityIds.length === 0 ||
                filters.categories.length === 0 ||
                filters.statuses.length === 0
              }
              variant="primary"
              colorScheme="v2-aurora"
              onClick={() => {
                onFilterChange({
                  ...filters,
                  loanIds:
                    filters.loanIds && filters.loanIds.length > 0
                      ? filters.loanIds
                      : undefined,
                });
                drawerControls.onClose();
              }}
            >
              Apply Filters
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
};
